import { ChevronDown } from "lucide-react";
import {
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "web-design-system";
import { useFundFiltersMetadata } from "creso-core/hooks";
import { DistinctFilterMetadata } from "creso-core/types";

type CategoryOption = DistinctFilterMetadata["values"][number];

// Sub-categories come tagged with their broad category (Equity, Debt, ...).
export function useCategoryOptions() {
  const { data, isPending } = useFundFiltersMetadata([
    "fund_level_category_name",
  ]);
  const options =
    data?.find((m): m is DistinctFilterMetadata => m.type === "distinct")
      ?.values ?? [];
  return { options, isPending };
}

// Review lines label categories as "Equity - Small Cap Fund".
export function findCategoryByLabel(options: CategoryOption[], label: string) {
  const [group, ...rest] = label.split(" - ");
  const name = rest.join(" - ");
  return (
    options.find((o) => o.group === group && o.name === name) ??
    options.find((o) => o.name === (name || group))
  );
}

// An empty selection means every category.
export function CategoryPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const { options } = useCategoryOptions();
  const groups = Object.entries(
    options.reduce<Record<string, CategoryOption[]>>((acc, option) => {
      const group = option.group ?? "Other";
      (acc[group] ??= []).push(option);
      return acc;
    }, {})
  );
  const label =
    value.length === 0
      ? "All"
      : value.length === 1
        ? (options.find((o) => o.id === value[0])?.name ?? "1 selected")
        : `${value.length} selected`;

  const toggle = (ids: string[], checked: boolean) =>
    onChange(
      checked
        ? Array.from(new Set([...value, ...ids]))
        : value.filter((id) => !ids.includes(id))
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="min-w-46 justify-between">
          Category: {label}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      {/* Kept inside the dialog: its scroll lock blocks wheel events on
          portalled content. */}
      <PopoverContent
        portal={false}
        align="start"
        className="flex max-h-96 z-51 w-72 flex-col gap-1 overflow-auto p-2"
      >
        <div className="flex items-center justify-between px-2 py-1">
          <span className="text-xs text-muted-foreground">Categories</span>
          <Button
            variant="link"
            size="sm"
            className="px-0"
            disabled={!value.length}
            onClick={() => onChange([])}
          >
            Clear
          </Button>
        </div>
        {groups.map(([group, subcategories]) => {
          const ids = subcategories.map((o) => o.id);
          const selectedCount = ids.filter((id) => value.includes(id)).length;
          return (
            // Groups holding a selection start expanded.
            <Collapsible
              key={group}
              defaultOpen={selectedCount > 0}
              className="group/category"
            >
              <div className="flex items-center gap-3 rounded-sm px-2 py-1.5 text-sm">
                <Checkbox
                  aria-label={group}
                  checked={
                    selectedCount === ids.length
                      ? true
                      : selectedCount > 0
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(checked) => toggle(ids, checked === true)}
                />
                <CollapsibleTrigger className="flex flex-1 items-center justify-between">
                  {group}
                  <ChevronDown className="size-4 text-muted-foreground group-data-[state=open]/category:rotate-180" />
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent className="flex flex-col pl-6">
                {subcategories.map((option) => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center gap-3 rounded-sm px-2 py-1.5 text-sm"
                  >
                    <Checkbox
                      checked={value.includes(option.id)}
                      onCheckedChange={(checked) =>
                        toggle([option.id], checked === true)
                      }
                    />
                    {option.name}
                  </label>
                ))}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
